import { useState } from "react";
import { Button } from "react-aria-components";
import { useGetConversationEvidenceQuery } from "@/features/chat/conversationApi";
import type { ConversationEvidenceEntry } from "@/features/chat/conversationSchemas";

function evidenceQualificationLabel(status: ConversationEvidenceEntry["support_status"]) {
  return { DIRECT_SUPPORT: "直接支持", DIRECT_OBSERVATION: "直接观测，不证明因果", TRANSFERABLE_HYPOTHESIS: "类比，待验证", HYPOTHESIS: "假设", UNVERIFIED: "资格未核实" }[status];
}

export function ConversationEvidenceDetails({ conversationId, organizationId, shopBindingId, assistantMessageId, disabled }: {
  conversationId: string; organizationId: string; shopBindingId: string; assistantMessageId: string; disabled: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [cursors, setCursors] = useState([""]);
  const cursor = cursors.at(-1) ?? "";
  const query = useGetConversationEvidenceQuery({ conversationId, organizationId, shopBindingId, assistantMessageId, cursor }, { skip: !open || disabled });
  // currentData只属于本次页/消息身份，不用迟到的上一页内容替新页。
  const page = query.currentData;
  return <section aria-label="回答依据详情">
    <Button aria-expanded={open} isDisabled={disabled} onPress={() => { setOpen(!open); if (open) setCursors([""]); }}>依据详情</Button>
    {open ? <div>
      {query.isFetching ? <p role="status">正在读取依据…</p> : query.isError ? <div role="alert">依据读取失败。<Button onPress={() => { void query.refetch(); }}>重试</Button>{cursor ? <Button onPress={() => setCursors([""])}>重新读取第一页</Button> : null}</div> : page ? <>
        <p>共 {page.total_count} 项，本页 {page.shown_count} 项{page.omitted_count ? `，后续 ${page.omitted_count} 项` : ""}。</p>
        {page.upstream_truncated ? <p>上游已声明材料不完整；此列表不是全部资料。</p> : null}
        {page.status === "empty" ? <p>这份回执没有可展示的证据材料。</p> : null}
        {page.status === "legacy_unknown" ? <p>这份较早的回答暂时无法展开依据详情。</p> : null}
        {page.status === "schema_unverified" ? <p>部分材料的关联尚未核实，以下资料请结合限制查看。</p> : null}
        {page.status === "projection_too_large" ? <p>资料过多，暂时无法完整展示。</p> : null}
        <ol>{page.entries.map((entry) => <li key={`${entry.tool_call_id ?? entry.delivery_batch_id}:${entry.evidence_id}`}>
          <strong>{evidenceQualificationLabel(entry.support_status)}</strong>
          <p>{entry.summary || "材料未提供摘要"}</p>
          <p>关联结论：{entry.claim_ids.length ? `${entry.claim_ids.length} 项` : "尚未核实"}</p>
          <p>平台：{entry.scope.platform || "未知"}；市场：{entry.scope.country_scope ? entry.scope.country_scope.kind === "GLOBAL" ? "全球" : entry.scope.country_scope.kind === "UNKNOWN" ? "未知" : entry.scope.country_scope.country_codes.map(countryLabel).join("、") || "未知" : countryLabel(entry.scope.country)}</p>
          {entry.scope_unmapped_count ? <p>还有部分适用范围无法完整展示。</p> : null}
          {entry.valid_time ? <p>来源标注的适用时间：{entry.valid_time}</p> : null}
          {entry.reference_period ? <p>资料参考期间：{entry.reference_period}</p> : null}
          {entry.period_start || entry.period_end ? <p>样本期间：{entry.period_start || "未知"} 至 {entry.period_end || "未知"}</p> : null}
          {entry.source_excerpt ? <p style={{ whiteSpace: "pre-wrap" }}>来源摘录：{entry.source_excerpt}</p> : null}
          {entry.logic ? <p>依据逻辑：{entry.logic}</p> : null}
          {entry.conditions ? <p style={{ whiteSpace: "pre-wrap" }}>适用条件：{entry.conditions}</p> : null}
          {entry.limitations.map((limitation, index) => <p key={index} style={{ whiteSpace: "pre-wrap" }}>限制：{limitation}</p>)}
          {entry.source_urls.length ? <ul>{entry.source_urls.map((url) => <li key={url}><a href={url} rel="noopener noreferrer" target="_blank">{entry.source_title || "来源原文"}</a></li>)}</ul> : <p>材料未提供可公开的网址。</p>}
        </li>)}</ol>
        {cursors.length > 1 ? <Button onPress={() => setCursors((current) => current.slice(0, -1))}>上一页</Button> : null}
        {page.next_cursor ? <Button onPress={() => setCursors((current) => [...current, page.next_cursor])}>下一页</Button> : null}
      </> : null}
    </div> : null}
  </section>;
}

function countryLabel(code?: string) {
  if (!code) return "未知";
  try { return new Intl.DisplayNames(["zh-CN"], { type: "region" }).of(code) ?? code; } catch { return code; }
}
